import { initialize } from 'launchdarkly-js-client-sdk';
import appConfig from 'appConfig';
import featureKeys from './featureKeys';
import { createHarness } from './harness';

/**
 * Unified feature flags: one in-memory map (`featureFlags`) keyed by LaunchDarkly-style names
 * so CDP/plugins stay stable. When `SERVE_FROM_LAUNCHDARKLY` is strictly `true` in LD, values are
 * LD-only. When false, LaunchDarkly remains the baseline stream, then Harness overlays boolean
 * snapshot keys using `defaultHarnessKeyFromLdKey` (dots → hyphens; `_`/`-` unchanged).
 *
 * ---------------------------------------------------------------------------
 * LaunchDarkly decommission (future cleanup)
 * ---------------------------------------------------------------------------
 * 1. Remove `launchdarkly-js-client-sdk` import and `appConfig.getLaunchDarklyClientId()` usage.
 * 2. Delete the whole `=== LaunchDarkly` section (client, listeners, routing flag, snapshots).
 * 3. In `initializeFeatureFlags`, drop LD bootstrap; set `featureFlags` to `{}` or static defaults,
 *    then always `await setupHarness(user)` (or a renamed single-provider init).
 * 4. Remove `shouldServeMappedFlagsFromLaunchDarkly`, `syncHarnessWithRoutingFlag`, and
 *    `featureKeys.SERVE_FROM_LAUNCHDARKLY` from `featureKeys.js`.
 * 5. Drop routing flag / sync helpers if Harness becomes the only provider.
 */
export default (() => {
  const LD_ROUTING_FLAG_KEY = featureKeys.SERVE_FROM_LAUNCHDARKLY;
  let featureFlags = {};
  let _subscriptions = [];
  let isInitialized = false;
  let isInitializedPromiseResolve;
  let lastUserForHarness = null;

  let isInitializedPromise = new Promise((resolve) => {
    isInitializedPromiseResolve = resolve;
  });

  // -------------------------------------------------------------------------
  // Shared
  // -------------------------------------------------------------------------

  function notifyListeners(featureFlagThatChanged) {
    _subscriptions.forEach((item) => {
      if (item.flag === featureFlagThatChanged) {
        item.callback(
          Object.freeze({
            flagName: featureFlagThatChanged,
            value: featureFlags[featureFlagThatChanged],
          })
        );
      }
    });
  }

  function unsubscribe(flag) {
    _subscriptions = _subscriptions.filter((sub) => sub.flag !== flag);
  }

  const {
    teardownHarness,
    setupHarness,
    reapplyHarnessOverlayAndNotify,
    getHarnessBooleanForLdStyleKey,
  } = createHarness({
    getFeatureFlags: () => featureFlags,
    notifyListeners,
  });

  // -------------------------------------------------------------------------
  // LaunchDarkly — delete section when LD is decommissioned
  // -------------------------------------------------------------------------

  let ldclient = null;

  /** Only strict `true` uses LD only; `false` or any other value keeps LD baseline and overlays Harness. */
  function shouldServeMappedFlagsFromLaunchDarkly() {
    return featureFlags[LD_ROUTING_FLAG_KEY] === true;
  }

  function refreshFeatureFlagsFromLaunchDarklySnapshot() {
    if (ldclient) {
      featureFlags = { ...ldclient.allFlags() };
    }
  }

  function buildLaunchDarklyUser(user) {
    if (!user) {
      return { anonymous: true };
    }
    return {
      key: user.accountId || '',
      firstName: user.givenName || '',
      lastName: user.familyName || '',
    };
  }

  async function createLaunchDarklyClientAndLoadFlags(user) {
    const ldOptions = {
      evaluationReasons: true,
      bootstrap: 'localstorage',
    };
    const client = initialize(
      appConfig.getLaunchDarklyClientId(),
      buildLaunchDarklyUser(user),
      ldOptions
    );
    await client.waitForInitialization();
    return { client, flags: { ...client.allFlags() } };
  }

  async function syncHarnessWithRoutingFlag(user) {
    if (shouldServeMappedFlagsFromLaunchDarkly()) {
      await teardownHarness();
      refreshFeatureFlagsFromLaunchDarklySnapshot();
      Object.keys(featureFlags).forEach((k) => notifyListeners(k));
    } else {
      await teardownHarness();
      await setupHarness(user);
      Object.keys(featureFlags).forEach((k) => notifyListeners(k));
    }
  }

  function handleLaunchDarklyFlagChange(updatedFlagDetails) {
    const keys = Object.keys(updatedFlagDetails);
    const routingKey = LD_ROUTING_FLAG_KEY;

    if (routingKey && keys.includes(routingKey)) {
      featureFlags[routingKey] = updatedFlagDetails[routingKey].current;
      notifyListeners(routingKey);
    }

    const serveFromLd = shouldServeMappedFlagsFromLaunchDarkly();

    keys.forEach((key) => {
      if (routingKey && key === routingKey) {
        return;
      }
      featureFlags[key] = updatedFlagDetails[key].current;
      if (serveFromLd) {
        notifyListeners(key);
      }
    });

    const routingChanged = Boolean(routingKey && keys.includes(routingKey));
    if (routingChanged) {
      void syncHarnessWithRoutingFlag(lastUserForHarness);
    } else if (!serveFromLd) {
      reapplyHarnessOverlayAndNotify();
    }
  }

  function attachLaunchDarklyChangeListener() {
    if (!ldclient) {
      return;
    }
    ldclient.on('change', handleLaunchDarklyFlagChange);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  async function initializeFeatureFlags(user) {
    lastUserForHarness = user === undefined ? null : user;

    if (ldclient) {
      ldclient.close();
    }
    await teardownHarness();

    const { client, flags } = await createLaunchDarklyClientAndLoadFlags(user);
    ldclient = client;
    featureFlags = flags;

    if (!shouldServeMappedFlagsFromLaunchDarkly()) {
      await setupHarness(user);
    }

    if (!isInitialized) {
      isInitializedPromiseResolve();
      isInitialized = true;
    }

    _subscriptions.forEach((item) => {
      notifyListeners(item.flag);
    });

    attachLaunchDarklyChangeListener();

    return Object.freeze({ ...featureFlags });
  }

  function get() {
    if (!isInitialized) {
      throw new Error(
        "You tried to get the feature flags before the feature flag service got initialized"
      );
    }
    return Object.freeze({ ...featureFlags });
  }

  async function getWhenReady() {
    await isInitializedPromise;
    return Object.freeze({ ...featureFlags });
  }

  /**
   * @deprecated for React Components.
   * Use getFeatureFlagValue via 'hocs/enhanceWithFeatureFlagSubscription'.
   */
  function is(featureName) {
    if (!isInitialized) {
      throw new Error(`You tried to check the status of ${featureName} before the service got initialized`);
    }
    if (featureFlags.hasOwnProperty(featureName)) {
      return featureFlags[featureName];
    }
    if (shouldServeMappedFlagsFromLaunchDarkly()) {
      throw new Error(`you tried to retrieve the status of an unknown feature flag ${featureName}`);
    }
    if (featureName === LD_ROUTING_FLAG_KEY) {
      throw new Error(`you tried to retrieve the status of an unknown feature flag ${featureName}`);
    }
    const harnessResult = getHarnessBooleanForLdStyleKey(featureName);
    if (harnessResult === null) {
      throw new Error(
        `Feature flag Harness is not available to evaluate ${featureName}; try again after initialization completes`
      );
    }
    if (!harnessResult.inHarness) {
      throw new Error(`you tried to retrieve the status of an unknown feature flag ${featureName}`);
    }
    return harnessResult.value;
  }

  function set(data) {
    const { name, isSet } = data;
    featureFlags[name] = isSet;
    notifyListeners(name);
    return featureFlags[name];
  }

  function subscribe(featureFlag, callbackFn) {
    const newSubscription = {
      flag: featureFlag,
      callback: callbackFn,
    };

    _subscriptions.push(newSubscription);

    isInitializedPromise.then(() => {
      notifyListeners(featureFlag);
    });

    return ((flag) => unsubscribe(flag));
  }

  return {
    initializeFeatureFlags,
    subscribe,
    get,
    getWhenReady,
    is,
    set,
    unsubscribe,
  };
})();

export { featureKeys };
